import React, { useEffect, useCallback, useState, useRef, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Pagination,
  useTheme,
  useMediaQuery,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Tooltip,
  SortDirection,
  IconButton,
  Collapse,
  TableSortLabel,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  selectMilestoneTasks,
  selectTaskReportsLoading,
  selectTaskReportsPagination,
  selectTaskReportsSortConfig,
  selectTaskReportsSortApplied,
  setPage,
  setPageSize,
  setSortConfig,
  calculateBudgetaryPerformance,
  calculateBudgetaryPerformanceWithoutPercentage,
  selectTotalItems,
  sortTaskReports,
  getDeveloperTimeBreakdown,
  getLatestPercentageComplete,
  formatTypeOfWork,
  getTypeOfWorkBreakdown,
} from "../../features/taskReports/taskReportsSlice";
import {
  selectNonReadSelectedMilestoneTaskEstimateAlerts,
  selectNonReadPercentageCompleteChangedAlerts,
  addClockifyTimeEntryTimeProcessedAlertIds,
  addMilestoneTaskEstimateProcessedAlertIds,
  addPercentageCompleteProcessedAlertIds,
  selectClockifyTimeEntryTimeChangedReadAlerts,
  selectMilestoneEstimateProcessedAlertIds,
  selectPercentageCompleteChangedReadAlertsIds,
  selectNonReadClockifyTimeChangedAlerts,
} from "../../features/alerts/alertsSlice";
import { addAlertReadEventAsync } from "../../features/alerts/alertsApi";
import { AlertTypeContract } from "../../features/alerts/AlertTypeContract";
import { MilestoneTask } from "../../features/taskReports/MilestoneTask";
import { HeadCell } from "./HeadCell";
import ExportToExcelButton from "../exportToExcelButton/ExportToExcelButton";
import { selectSelectedMilestone } from "../../features/projects/projectSlice";
import TimeFormatter from "../../features/extensions/TimeFormatter";
import { fetchTaskReportsAsync } from "../../features/taskReports/taskReportsApi";
import { SortConfig } from "../../features/taskReports/SortConfig";
import { SortField } from "../../features/taskReports/SortField";
import DeveloperTimeAllocation from "../developerTimeAllocation/DeveloperTimeAllocation";
import { MilestoneTaskReportExcelExporter } from "../../features/taskReports/MilestoneTaskReportExcelExporter";
import TaskReportTimeEntriesTable from "./TaskReportTimeEntriesTable";
import TaskReportAlert from "../taskReportAlert/TaskReportAlert";

interface TaskReportTableProps {
  milestoneId?: number;
  taskReports?: MilestoneTask[];
  loading?: boolean;
  pagination?: {
    currentPage: number;
    pageSize: number;
    totalPages: number;
    totalItems: number;
  };
  sortConfig?: SortConfig;
  sortApplied?: boolean;
  onFetchTaskReports?: (milestoneId: number) => void;
  onSetPage?: (page: number) => void;
  onSetPageSize?: (pageSize: number) => void;
  onSetSortConfig?: (config: SortConfig) => void;
  onSortTaskReports?: () => void;
  isExpanded?: boolean;
  showExpandButton?: boolean;
  onExpandClick?: () => void;
}

const headCells: HeadCell[] = [
  { id: "gitLabTaskNumber", label: "Task Number", numeric: true, width: "8%" },
  { id: "taskTitle", label: "Task Title", numeric: false, width: "22%" },
  { id: "developerNames", label: "Developer(s)", numeric: false, width: "5%" },
  { id: "typeOfWork", label: "Type Of Work", numeric: false, width: "9%" },
  {
    id: "totalTimeSpentOnTask",
    label: "Total Time",
    numeric: true,
    width: "8%",
  },
  { id: "timeEstimate", label: "Time Budget", numeric: true, width: "11%" },
  {
    id: "budgetaryPerformance",
    label: "Budgetary Performance",
    numeric: true,
    width: "6%",
  },
  { id: "dateRange", label: "Date Range", numeric: false, width: "10%" },
  {
    id: "percentageComplete",
    label: "% Complete",
    numeric: true,
    width: "9%",
  },
  {
    id: "expand",
    label: "",
    numeric: false,
    width: "3%",
    disableSorting: true,
  },
];

const pageSizeOptions = [10, 20, 50, 100];

const TaskReportTable = ({
  milestoneId,
  taskReports: propTaskReports,
  loading: propLoading,
  pagination: propPagination,
  sortConfig: propSortConfig,
  sortApplied: propSortApplied,
  onFetchTaskReports,
  onSetPage,
  onSetPageSize,
  onSetSortConfig,
  onSortTaskReports,
  isExpanded = true,
  showExpandButton = false,
  onExpandClick,
}: TaskReportTableProps) => {
  const defaultSortConfig: SortConfig = {
    field: "dateRange",
    direction: "desc",
  };
  const dispatch = useAppDispatch();
  const tableRef = useRef<HTMLDivElement>(null);
  const processedAlertsRef = useRef<{
    clockify: number[];
    estimate: number[];
    percentage: number[];
  }>({
    clockify: [],
    estimate: [],
    percentage: [],
  });

  const [isExportToExcelLoading, setIsExportToExcelLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState<{ [key: number]: boolean }>(
    {}
  );

  const reduxTaskReports = useAppSelector(selectMilestoneTasks);
  const reduxLoading = useAppSelector(selectTaskReportsLoading);
  const reduxPagination = useAppSelector(selectTaskReportsPagination);
  const reduxSortConfig = useAppSelector(selectTaskReportsSortConfig);
  const reduxSortApplied = useAppSelector(selectTaskReportsSortApplied);
  const reduxTotalItems = useAppSelector(selectTotalItems);
  const selectedMilestone = useAppSelector(selectSelectedMilestone);
  const clockifyTimeChangedReadAlerts = useAppSelector(
    selectClockifyTimeEntryTimeChangedReadAlerts
  );
  const milestoneTaskEstimateReadAlerts = useAppSelector(
    selectMilestoneEstimateProcessedAlertIds
  );
  const percentageCompleteReadAlertIds = useAppSelector(
    selectPercentageCompleteChangedReadAlertsIds
  );
  const clockifyTimeChangedAlerts = useAppSelector(
    selectNonReadClockifyTimeChangedAlerts
  );
  const estimateChangedAlerts = useAppSelector(
    selectNonReadSelectedMilestoneTaskEstimateAlerts
  );
  const percentageChangedAlerts = useAppSelector(
    selectNonReadPercentageCompleteChangedAlerts
  );

  // Then use props or Redux state
  const taskReports = propTaskReports || reduxTaskReports;
  const loading = propLoading !== undefined ? propLoading : reduxLoading;
  const pagination = propPagination || reduxPagination;
  const sortConfig = propSortConfig || reduxSortConfig || defaultSortConfig;
  const sortApplied =
    propSortApplied !== undefined ? propSortApplied : reduxSortApplied;
  const totalItems = propTaskReports ? propTaskReports.length : reduxTotalItems;

  // Add pagination logic
  const startIndex = (pagination.currentPage - 1) * pagination.pageSize;
  const endIndex = startIndex + pagination.pageSize;
  const paginatedTaskReports = taskReports.slice(startIndex, endIndex);

  const shouldShowTypeOfWork = useMemo(() => {
    if (taskReports.length === 0) return true;
    return taskReports.some(
      (task) => formatTypeOfWork(task) !== "N/A"
    );
  }, [taskReports]);

  const shouldShowPercentageComplete = useMemo(() => {
    if (taskReports.length === 0) return true;
    return taskReports.some(
      (task) => {
        const percentageComplete = getLatestPercentageComplete(task);
        return percentageComplete !== undefined && percentageComplete !== null && percentageComplete > 0;
      }
    );
  }, [taskReports]);

  const visibleHeadCells = useMemo(() => {
    let filtered = headCells;
    if (!shouldShowTypeOfWork) {
      filtered = filtered.filter((cell) => cell.id !== "typeOfWork");
    }
    if (!shouldShowPercentageComplete) {
      filtered = filtered.filter((cell) => cell.id !== "percentageComplete");
    }
    return filtered;
  }, [shouldShowTypeOfWork, shouldShowPercentageComplete]);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const timeFormatter = new TimeFormatter();

  const handleSortRequest = useCallback(
    (field: SortField) => {
      let direction: SortDirection = "desc";

      if (sortConfig.field === field) {
        direction = sortConfig.direction === "asc" ? "desc" : "asc";
      }

      const newSortConfig = { field, direction };

      if (onSetSortConfig) {
        onSetSortConfig(newSortConfig);
      } else {
        dispatch(setSortConfig(newSortConfig));
      }

      if (onSortTaskReports) {
        onSortTaskReports();
      } else {
        dispatch(sortTaskReports());
      }
    },
    [dispatch, sortConfig, onSetSortConfig, onSortTaskReports]
  );

  useEffect(() => {
    if (typeof milestoneId === "number" && milestoneId > 0) {
      if (onFetchTaskReports) {
        onFetchTaskReports(milestoneId);
      } else {
        dispatch(fetchTaskReportsAsync(milestoneId));
      }
    }
  }, [dispatch, milestoneId, onFetchTaskReports]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    if (onSetPage) {
      onSetPage(page);
    } else {
      dispatch(setPage(page));
    }
  };

  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    const newSize = Number(event.target.value);
    if (onSetPageSize) {
      onSetPageSize(newSize);
    } else {
      dispatch(setPageSize(newSize));
    }
  };

  const formatDateRange = (task: MilestoneTask) => {
    if (!task.taskReports || task.taskReports.length === 0) {
      return "N/A";
    }

    const startDates = task.taskReports.map(
      (report) => new Date(report.dateStart)
    );
    const endDates = task.taskReports.map((report) => new Date(report.dateEnd));

    const earliestStart = new Date(
      Math.min(...startDates.map((date) => date.getTime()))
    );
    const latestEnd = new Date(
      Math.max(...endDates.map((date) => date.getTime()))
    );

    return `${earliestStart.toLocaleDateString()} - ${latestEnd.toLocaleDateString()}`;
  };

  const formatDevelopers = (task: MilestoneTask): string => {
    if (!task.taskReports || task.taskReports.length === 0) {
      return "N/A";
    }

    const developerTimeBreakdown = getDeveloperTimeBreakdown(task);
    const developerNames = developerTimeBreakdown
      .split("\n")
      .map((entry) => entry.split(": ")[0])
      .join(", ");

    return developerNames || "N/A";
  };

  const calculateTotalTimeSpent = (task: MilestoneTask) => {
    if (!task.taskReports || task.taskReports.length === 0) {
      return 0;
    }

    return task.taskReports.reduce(
      (total, report) => total + report.totalTimeSpentOnTask,
      0
    );
  };

  const formatBudgetPerformance = (value: number) => {
    return value.toFixed(2);
  };

  const handleRowExpand = (taskId: number) => {
    setExpandedRows((prev) => ({
      ...prev,
      [taskId]: !prev[taskId],
    }));
  };

  const hasEstimateChanged = (taskId: number) => {
    return estimateChangedAlerts.some(
      (alert) => alert.milestoneTaskId === taskId
    );
  };

  const hasPercentageChanged = (taskId: number) => {
    return percentageChangedAlerts.some(
      (alert) => alert.milestoneTaskId === taskId
    );
  };

  const getTaskEstimateChangedAlerts = (taskId: number) => {
    return estimateChangedAlerts.filter(
      (alert) => alert.milestoneTaskId === taskId
    );
  };

  const renderAlertsTooltip = (taskId: number) => {
    const estimateAlerts = getTaskEstimateChangedAlerts(taskId);
    const percentageAlerts = percentageChangedAlerts.filter(
      (alert) => alert.milestoneTaskId === taskId
    );

    if (estimateAlerts.length === 0 && percentageAlerts.length === 0) {
      return null;
    }

    return (
      <TaskReportAlert
        estimateAlerts={estimateAlerts}
        percentageAlerts={percentageAlerts}
      />
    );
  };

  const sendProcessedAlerts = useCallback(() => {
    if (processedAlertsRef.current.clockify.length > 0) {
      const request = {
        readAlertIds: processedAlertsRef.current.clockify,
        alertType: AlertTypeContract.TimeEntryTime,
      };
      dispatch(addAlertReadEventAsync(request));
      dispatch(
        addClockifyTimeEntryTimeProcessedAlertIds(
          processedAlertsRef.current.clockify
        )
      );
    }

    if (processedAlertsRef.current.estimate.length > 0) {
      const request = {
        readAlertIds: processedAlertsRef.current.estimate,
        alertType: AlertTypeContract.MilestoneTaskEstimate,
      };
      dispatch(addAlertReadEventAsync(request));
      dispatch(
        addMilestoneTaskEstimateProcessedAlertIds(
          processedAlertsRef.current.estimate
        )
      );
    }

    if (processedAlertsRef.current.percentage.length > 0) {
      const request = {
        readAlertIds: processedAlertsRef.current.percentage,
        alertType: AlertTypeContract.PercentageComplete,
      };
      dispatch(addAlertReadEventAsync(request));
      dispatch(
        addPercentageCompleteProcessedAlertIds(
          processedAlertsRef.current.percentage
        )
      );
    }
  }, [dispatch]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
              const visibleTaskIds = taskReports.map((task) => task.id);

              const visibleClockifyAlerts = clockifyTimeChangedAlerts.filter(
                (alert) =>
                  visibleTaskIds.includes(alert.clockifyTimeEntryId) &&
                  !clockifyTimeChangedReadAlerts.some(
                    (readAlert) => readAlert.id === alert.id
                  ) &&
                  !processedAlertsRef.current.clockify.includes(alert.id)
              );

              if (visibleClockifyAlerts.length > 0) {
                processedAlertsRef.current.clockify = [
                  ...processedAlertsRef.current.clockify,
                  ...visibleClockifyAlerts.map((alert) => alert.id),
                ];
              }

              const visibleTaskEstimateAlerts = estimateChangedAlerts.filter(
                (alert) =>
                  visibleTaskIds.includes(alert.milestoneTaskId) &&
                  !milestoneTaskEstimateReadAlerts.includes(alert.id) &&
                  !processedAlertsRef.current.estimate.includes(alert.id)
              );

              if (visibleTaskEstimateAlerts.length > 0) {
                processedAlertsRef.current.estimate = [
                  ...processedAlertsRef.current.estimate,
                  ...visibleTaskEstimateAlerts.map((alert) => alert.id),
                ];
              }

              const visiblePercentageAlerts = percentageChangedAlerts.filter(
                (alert) =>
                  visibleTaskIds.includes(alert.milestoneTaskId) &&
                  !percentageCompleteReadAlertIds.includes(alert.id) &&
                  !processedAlertsRef.current.percentage.includes(alert.id)
              );

              if (visiblePercentageAlerts.length > 0) {
                processedAlertsRef.current.percentage = [
                  ...processedAlertsRef.current.percentage,
                  ...visiblePercentageAlerts.map((alert) => alert.id),
                ];
              }

              if (
                visibleClockifyAlerts.length > 0 ||
                visibleTaskEstimateAlerts.length > 0 ||
                visiblePercentageAlerts.length > 0
              ) {
                sendProcessedAlerts();
              }
            }, 1000);
          }
        });
      },
      { threshold: 0.2, rootMargin: "30px" }
    );

    const currentTableRef = tableRef.current;
    if (currentTableRef) {
      observer.observe(currentTableRef);
    }

    return () => {
      if (currentTableRef) {
        observer.unobserve(currentTableRef);
      }
      clearTimeout(timeoutId);
    };
  }, [
    taskReports,
    clockifyTimeChangedAlerts,
    estimateChangedAlerts,
    percentageChangedAlerts,
    clockifyTimeChangedReadAlerts,
    milestoneTaskEstimateReadAlerts,
    percentageCompleteReadAlertIds,
    sendProcessedAlerts,
  ]);

  if (loading && !taskReports.length) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!taskReports.length) {
    return (
      <Box>
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, px: 0.5 }}
        >
          {showExpandButton && (
            <IconButton onClick={onExpandClick} size="small" sx={{ mr: 1 }}>
              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
          <Typography variant="h6" component="h2">
            Task Reports
          </Typography>
        </Box>
        <Collapse in={isExpanded}>
          <Typography sx={{ mt: 2 }}>
            No task reports available for this milestone.
          </Typography>
        </Collapse>
      </Box>
    );
  }
  const milestoneTaskReportExcelExporter =
    new MilestoneTaskReportExcelExporter();
  const handleExportToExcel = async () => {
    setIsExportToExcelLoading(true);
    try {
      milestoneTaskReportExcelExporter.exportToExcel(
        taskReports,
        `Task Reports - ${selectedMilestone?.name}`
      );
    } catch (error) {
      console.error("Error exporting to Excel:", error);
    } finally {
      setIsExportToExcelLoading(false);
    }
  };

  return (
    <Box sx={{ overflowX: "auto", px: 0.5 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          flexDirection: isSmallScreen ? "column" : "row",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {showExpandButton && (
            <IconButton onClick={onExpandClick} size="small" sx={{ mr: 1 }}>
              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
          <Typography variant="h6" component="h2">
            Task Reports
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mt: isSmallScreen ? 2 : 0,
            ml: "auto",
          }}
        >
          {typeof milestoneId === "number" && milestoneId > 0 && (
            <ExportToExcelButton
              isLoading={isExportToExcelLoading}
              onClick={handleExportToExcel}
            />
          )}
        </Box>
      </Box>
      <Collapse in={isExpanded}>
        <Paper
          elevation={3}
          sx={{ mb: 3, overflow: "hidden", position: "relative" }}
          ref={tableRef}
        >
          {loading && taskReports.length > 0 && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(13, 17, 23, 0.7)"
                    : "rgba(255, 255, 255, 0.7)",
                zIndex: 10,
              }}
            >
              <CircularProgress />
            </Box>
          )}
          <TableContainer
            sx={{
              height: {
                xs: 400,
                sm: 500,
                md: 600,
              },
              overflow: "auto",
              "& .MuiTableHead-root": {
                position: "sticky",
                top: 0,
                zIndex: 10,
              },
              "& .MuiCollapse-root .MuiTableHead-root": {
                position: "static",
                zIndex: 1,
              },
            }}
          >
            <Table aria-label="task reports table" stickyHeader>
              <TableHead>
                <TableRow>
                  {visibleHeadCells.map((headCell) => (
                    <TableCell
                      key={headCell.id}
                      align="left"
                      sortDirection={
                        sortConfig.field === headCell.id && sortApplied
                          ? sortConfig.direction
                          : false
                      }
                      sx={{
                        backgroundColor: theme.palette.background.paper,
                        position: "relative",
                        width: headCell.width,
                        pl: headCell.id === "expand" ? 1 : 2,
                        pr: 0,
                        zIndex: 2,
                        display:
                          (isSmallScreen && (headCell.id === "developerNames" || headCell.id === "typeOfWork")) ||
                          (headCell.id === "typeOfWork" && !shouldShowTypeOfWork)
                            ? "none"
                            : "table-cell",
                      }}
                      onClick={() =>
                        !headCell.disableSorting &&
                        handleSortRequest(headCell.id)
                      }
                    >
                      {!headCell.disableSorting ? (
                        <TableSortLabel
                          active={sortConfig.field === headCell.id}
                          direction={
                            sortConfig.field === headCell.id
                              ? sortConfig.direction
                              : "asc"
                          }
                        >
                          {headCell.label}
                        </TableSortLabel>
                      ) : (
                        headCell.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedTaskReports.map((task: MilestoneTask) => {
                  const totalTimeSpent = calculateTotalTimeSpent(task);
                  const budgetaryPerformance = shouldShowPercentageComplete
                    ? calculateBudgetaryPerformance(task)
                    : calculateBudgetaryPerformanceWithoutPercentage(task);
                  const percentageComplete = getLatestPercentageComplete(task);
                  const hasTimeEntries =
                    task.timeEntries && task.timeEntries.length > 0;
                  const isExpanded = !!expandedRows[task.id];
                  const hasEstimateChange = hasEstimateChanged(task.id);
                  const hasPercentageChange = hasPercentageChanged(task.id);

                  return (
                    <React.Fragment key={task.id}>
                      <Tooltip
                        title={renderAlertsTooltip(task.id)}
                        placement="top"
                        arrow
                        disableHoverListener={
                          !hasEstimateChange && !hasPercentageChange
                        }
                        componentsProps={{
                          tooltip: {
                            sx: {
                              bgcolor: "transparent",
                              p: 0,
                              maxWidth: "max-content",
                              "& .MuiTooltip-arrow": {
                                color: "warning.light",
                                "&::before": {
                                  bgcolor: "rgba(255, 244, 229, 0.98)",
                                  border: "1px solid",
                                  borderColor: "warning.light",
                                },
                              },
                            },
                          },
                        }}
                      >
                        <TableRow
                          hover
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                            backgroundColor:
                              hasEstimateChange || hasPercentageChange
                                ? "rgba(255, 152, 0, 0.1)"
                                : "inherit",
                            "&:hover": {
                              backgroundColor:
                                hasEstimateChange || hasPercentageChange
                                  ? "rgba(255, 152, 0, 0.2)"
                                  : "inherit",
                            },
                          }}
                        >
                          <TableCell align="left">
                            {task.gitLabTaskNumber || "N/A"}
                          </TableCell>
                          <TableCell align="left">
                            {task.taskTitle && task.taskTitle.length > 55 ? (
                              <Tooltip
                                title={
                                  <Typography
                                    variant="body2"
                                    style={{ whiteSpace: "pre-line" }}
                                  >
                                    {task.taskTitle}
                                  </Typography>
                                }
                                arrow
                                placement="bottom"
                              >
                                <Typography
                                  sx={{
                                    display: "-webkit-box",
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    lineHeight: 1.43,
                                    letterSpacing: "0.01071em",
                                    fontWeight: 400,
                                    fontSize: "0.875rem",
                                  }}
                                >
                                  {task.taskTitle}
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Typography
                                sx={{
                                  display: "-webkit-box",
                                  WebkitLineClamp: 3,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  lineHeight: 1.43,
                                  letterSpacing: "0.01071em",
                                  fontWeight: 400,
                                  fontSize: "0.875rem",
                                }}
                              >
                                {task.taskTitle || "N/A"}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{
                              display: isSmallScreen ? "none" : "table-cell",
                            }}
                          >
                            <Tooltip
                              title={
                                <Typography
                                  variant="body2"
                                  style={{ whiteSpace: "pre-line" }}
                                >
                                  {getDeveloperTimeBreakdown(task)}
                                </Typography>
                              }
                              arrow
                              placement="bottom"
                            >
                              <Box>
                                <Box component="span">
                                  {formatDevelopers(task)}
                                </Box>
                                <DeveloperTimeAllocation task={task} />
                              </Box>
                            </Tooltip>
                          </TableCell>
                          {shouldShowTypeOfWork && (
                            <TableCell
                              align="left"
                              sx={{
                                display: isSmallScreen ? "none" : "table-cell",
                              }}
                            >
                              <Tooltip
                                title={
                                  <Typography
                                    variant="body2"
                                    style={{ whiteSpace: "pre-line" }}
                                  >
                                    {getTypeOfWorkBreakdown(task)}
                                  </Typography>
                                }
                                arrow
                                placement="bottom"
                              >
                                <Box component="span">
                                  {formatTypeOfWork(task)}
                                </Box>
                              </Tooltip>
                            </TableCell>
                          )}
                          <TableCell align="right">
                            <Box
                              component="span"
                              sx={{
                                color: (theme) =>
                                  typeof task.timeEstimate === "number" &&
                                  totalTimeSpent > task.timeEstimate
                                    ? theme.palette.warning.main
                                    : "inherit",
                              }}
                            >
                              {timeFormatter.toHoursAndMinutes(totalTimeSpent)}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            {typeof task.timeEstimate === "number" &&
                            task.timeEstimate
                              ? timeFormatter.toHoursAndMinutes(
                                  task.timeEstimate
                                )
                              : "N/A"}
                          </TableCell>
                          <TableCell align="right">
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                border: "1px solid",
                                borderColor:
                                  budgetaryPerformance === 0
                                    ? "grey"
                                    : budgetaryPerformance <= 1
                                    ? "success.main"
                                    : budgetaryPerformance <= 1.25
                                    ? "warning.main"
                                    : "error.main",
                                borderRadius: "16px",
                                padding: "0 8px",
                                height: "24px",
                                lineHeight: "20px",
                                color:
                                  budgetaryPerformance === 0
                                    ? "grey"
                                    : budgetaryPerformance <= 1
                                    ? "success.main"
                                    : budgetaryPerformance <= 1.25
                                    ? "warning.main"
                                    : "error.main",
                              }}
                            >
                              {formatBudgetPerformance(budgetaryPerformance)}
                            </Box>
                          </TableCell>
                          <TableCell align="left">
                            {formatDateRange(task)}
                          </TableCell>
                          {shouldShowPercentageComplete && (
                            <TableCell align="right">
                              <Box
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  border: "1px solid",
                                  borderColor:
                                    percentageComplete === 0
                                      ? "grey"
                                      : percentageComplete === 100
                                      ? "success.main"
                                      : percentageComplete >= 75
                                      ? "info.main"
                                      : percentageComplete >= 50
                                      ? "warning.main"
                                      : "primary.main",
                                  borderRadius: "16px",
                                  padding: "0 8px",
                                  height: "24px",
                                  lineHeight: "20px",
                                  color:
                                    percentageComplete === 0
                                      ? "grey"
                                      : percentageComplete === 100
                                      ? "success.main"
                                      : percentageComplete >= 75
                                      ? "info.main"
                                      : percentageComplete >= 50
                                      ? "warning.main"
                                      : "primary.main",
                                }}
                              >
                                {`${percentageComplete}%`}
                              </Box>
                            </TableCell>
                          )}
                          <TableCell padding="normal">
                            {hasTimeEntries && (
                              <IconButton
                                aria-label="expand row"
                                size="small"
                                onClick={() => handleRowExpand(task.id)}
                              >
                                {isExpanded ? (
                                  <KeyboardArrowUpIcon />
                                ) : (
                                  <KeyboardArrowDownIcon />
                                )}
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      </Tooltip>
                      {hasTimeEntries && (
                        <TableRow>
                          <TableCell
                            style={{
                              paddingBottom: 0,
                              paddingTop: 0,
                              borderBottom: isExpanded
                                ? "1px solid rgba(224, 224, 224, 1)"
                                : "0px",
                            }}
                            colSpan={
                              (shouldShowTypeOfWork ? 1 : 0) +
                              (shouldShowPercentageComplete ? 1 : 0) +
                              8
                            }
                          >
                            <Collapse
                              in={isExpanded}
                              timeout="auto"
                              unmountOnExit
                            >
                              <TaskReportTimeEntriesTable task={task} />
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              p: 2,
              borderTop: 1,
              borderColor: "divider",
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              gap: 2,
            }}
          >
            <Box sx={{ flex: { xs: "1", sm: "1 1 25%" } }}>
              <Typography variant="body2" color="text.secondary">
                Page {pagination.currentPage} of {pagination.totalPages} (
                {totalItems} items total)
              </Typography>
            </Box>

            <Box
              sx={{
                flex: { xs: "1", sm: "1 1 50%" },
                display: "flex",
                justifyContent: "center",
                order: { xs: 3, sm: 2 },
                mt: { xs: 2, sm: 0 },
              }}
            >
              <Pagination
                count={pagination.totalPages}
                page={pagination.currentPage}
                onChange={handlePageChange}
                color="primary"
                size={isSmallScreen ? "small" : "medium"}
                showFirstButton
                showLastButton
                siblingCount={isSmallScreen ? 0 : 1}
                sx={{
                  "& .MuiPaginationItem-root": {
                    minWidth: { xs: 30, sm: 32 },
                    height: { xs: 30, sm: 32 },
                  },
                }}
              />
            </Box>

            <Box
              sx={{
                flex: { xs: "1", sm: "1 1 25%" },
                display: "flex",
                justifyContent: { xs: "flex-start", sm: "flex-end" },
                order: { xs: 2, sm: 3 },
              }}
            >
              <FormControl
                variant="outlined"
                size="small"
                sx={{ minWidth: { xs: 150, sm: 120 } }}
              >
                <InputLabel id="page-size-select-label">
                  Items per page
                </InputLabel>
                <Select
                  labelId="page-size-select-label"
                  id="page-size-select"
                  value={pagination.pageSize}
                  onChange={handlePageSizeChange}
                  label="Items per page"
                >
                  {pageSizeOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Paper>
      </Collapse>
    </Box>
  );
};

export default TaskReportTable;
